import type { ObserverApproveBulkEnvelope } from '@/app/election-observer/bulk-assign';
import type {
	IObserverApplicationDetail,
	IObserverApplicationListParams,
	IObserverApplicationSummary,
	IObserverEntityListParams,
	IObserverLedgerRow,
	IObserverManagementScopeParams,
	IObserverReasonPayload,
	ObserverDivisionType,
} from '@/app/election-observer/management-types';
import { mockExportObserverApplications } from '@/app/election-observer/observer-management.mock';
import type { ICreateStateObserverPayload, ICreateStateObserverResult } from '@/app/election-observer/state-observer';
import { OBSERVER_RESULT_TYPE_PARAM, observerApplicationParams } from '@/app/election-observer/utils';
import type { IApiResponse } from '@/helpers/types';

import { coreApiSlice } from '../apiSlice';

export type IElectionObserverUser = Record<string, unknown>;

export interface IElectionObserver {
	id: number;
	organizationName: string;
	accreditationNumber: string;
	observerType: number;
	isAccredited: boolean;
	accreditedAt: string | null;
	accreditedBy: string | null;
	validFrom: string;
	validTo: string;
	contactEmail: string;
	contactPhone: string;
	description: string;
	urlGuid: string;
	dateCreated: string;
	users: IElectionObserverUser[];
}

export interface IElectionObserverListParams {
	PageNumber?: number;
	PageSize?: number;
	OrganizationName?: string;
	ObserverType?: number;
	isAccredited?: boolean;
}

export interface IElectionObserverPayload {
	organizationName: string;
	accreditationNumber: string;
	observerType: number;
	contactEmail: string;
	contactPhone: string;
	description: string;
	validFrom: string;
	validTo: string;
}

export interface IObserverScopeParams {
	electionId: number;
	electionObserverId: number;
}

/**
 * Registers ONE partner to ONE election. There is no bulk form, so a caller registering
 * several has to fan out one request per election and keep its own ledger of what each one
 * answered (`right-panel/election-observer/register-elections-run.ts`). The read counterpart
 * is `getObserverElections` below.
 */
export type IRegisterObserverElectionPayload = IObserverScopeParams;

/** One row of `GET /ElectionObserver/{id}/elections` — an election the partner is registered to. */
export interface IObserverElection {
	id: number;
	name: string;
	electionCycleId: number;
	constituencyId: number;
	constituency: string;
	electiveCategory: string;
	isActive: boolean;
	irevReference: string;
	/** A JS `Date.toString()` string ("Sat Jun 20 2026 00:00:00 GMT+0100 (…)"), not ISO. */
	date: string;
	stateId: number;
	state: string;
	isLocked: boolean;
	lockedPartyCount: number;
}

/**
 * Scope for the observer analytics reads. Both ids are REQUIRED and no geographic
 * filter is accepted by design: the ledger's sibling `ResultAnalytics/assignment-stats`
 * changes what `totalApplicantCount` counts once an entity filter is present, so a
 * caller that narrowed the query would get a differently-meaning denominator back.
 *
 * ASSUMPTION, unverified against staging: `assignment-stats` honours
 * `electionObserverId`. The sampled response summed to more entities than the
 * partner-scoped `/ObserverAnalytics` list reported for the same ledger, which is
 * also consistent with the two samples being different scopes. If the cards ever
 * read election-wide rather than partner-wide, this parameter is the first suspect.
 */
export interface IObserverAnalyticsParams {
	electionId: number;
	electionObserverId: number;
}

/**
 * One level's slice of `GET /ObserverAnalytics/assignment-stats`.
 *
 * Note this is a NARROWER shape than {@link IObserverCoverageLevel}: the ledger
 * endpoint reports no `totalCount`, `noApplicantsCount` or `applicationCount`,
 * so the entity denominator has to come from the coverage summary instead.
 */
export interface IObserverAssignmentStatsLevel {
	unassignedCount: number;
	readyToAssignCount: number;
	assignedCount: number;
	totalApplicantCount: number;
}

/** Ledger rollups keyed by division level — every level is always present. */
export type IObserverAssignmentStats = Record<ObserverDivisionType, IObserverAssignmentStatsLevel>;

/** `POST /ObserverApplication/expire-offered-links` — what the sweep just moved. Both empty = nothing lapsed. */
export interface IObserverOfferSweepResult {
	/** Now Offer Expired (5). */
	expiredIds: number[];
	/** Now Offer Sent (4), each with a fresh link and email. */
	promotedIds: number[];
}

type IObserverApplicationActionParams = IObserverManagementScopeParams & { id: number };
type IObserverReasonActionParams = IObserverManagementScopeParams & IObserverReasonPayload;
type IObserverBulkApproveParams = IObserverManagementScopeParams & { applicationIds: number[] };

/** Compatibility alias for the previous observer detail panel. */
export type IObserverVolunteer = IObserverApplicationDetail;

export const electionObserverApi = coreApiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getElectionObservers: builder.query<IApiResponse<IElectionObserver[]>, IElectionObserverListParams>({
			query: (params) => ({ url: '/ElectionObserver', params }),
			providesTags: ['ElectionObservers'],
		}),
		getElectionObserverById: builder.query<IApiResponse<IElectionObserver>, number>({
			query: (id) => ({ url: `/ElectionObserver/${id}` }),
			providesTags: ['ElectionObservers'],
		}),
		createElectionObserver: builder.mutation<IApiResponse, IElectionObserverPayload>({
			query: (body) => ({ url: '/ElectionObserver', method: 'POST', body }),
			invalidatesTags: ['ElectionObservers'],
		}),
		updateElectionObserver: builder.mutation<IApiResponse, { id: number } & IElectionObserverPayload>({
			query: ({ id, ...body }) => ({ url: `/ElectionObserver/${id}`, method: 'PUT', body }),
			invalidatesTags: ['ElectionObservers'],
		}),
		deleteElectionObserver: builder.mutation<IApiResponse, number>({
			query: (id) => ({ url: `/ElectionObserver/${id}`, method: 'DELETE' }),
			invalidatesTags: ['ElectionObservers'],
		}),
		accreditElectionObserver: builder.mutation<IApiResponse, number>({
			query: (id) => ({ url: `/ElectionObserver/${id}/accredit`, method: 'POST' }),
			invalidatesTags: ['ElectionObservers', 'ObserverManagement'],
		}),
		getObserverRegistrationLink: builder.query<IApiResponse<string>, IObserverScopeParams>({
			query: (params) => ({ url: '/ElectionObserver/get-registration-link', params }),
		}),
		generateObserverLink: builder.mutation<IApiResponse<string>, IObserverScopeParams>({
			query: (body) => ({ url: '/ElectionObserver/generate-link', method: 'POST', body }),
		}),
		deleteObserverLink: builder.mutation<IApiResponse, IObserverScopeParams>({
			query: (params) => ({ url: '/ElectionObserver/delete-link', method: 'DELETE', params }),
		}),
		/**
		 * The elections ONE partner is registered to. This — not the global active-elections
		 * list — is what the Manage Observers scope trail offers, so an operator can never scope
		 * to an election the partner holds no registration for. Tagged `ElectionObservers` so a
		 * registration from the Register Elections panel refreshes it.
		 */
		getObserverElections: builder.query<IApiResponse<IObserverElection[]>, number>({
			query: (id) => ({ url: `/ElectionObserver/${id}/elections` }),
			providesTags: ['ElectionObservers'],
		}),
		/**
		 * Partner in the PATH, election in the QUERY STRING, no body:
		 * `POST /ElectionObserver/{electionObserverId}/register-election?electionId=…`.
		 * A JSON body `{ electionObserverId, electionId }` binds to nothing here.
		 */
		registerObserverElection: builder.mutation<IApiResponse, IRegisterObserverElectionPayload>({
			query: ({ electionObserverId, electionId }) => ({
				url: `/ElectionObserver/${electionObserverId}/register-election`,
				method: 'POST',
				params: { electionId },
			}),
			// Refreshes `getObserverElections`, so the scope trail offers the new election at once.
			invalidatesTags: ['ElectionObservers'],
		}),

		/**
		 * The assignment ledger, one page at a time, scoped to a single division level.
		 *
		 * `ResultType` is what makes this a division view: the ledger holds one row per
		 * entity at EVERY level, so without it a polling-unit page would be interleaved
		 * with its wards. The value is lowercase on the wire (`ec8a`).
		 *
		 * Every focus filter maps onto a real server param, so paging and counts stay
		 * honest — nothing here is filtered client-side over a single page.
		 *
		 * `SortKey` is passed through unverified: the accepted tokens are not documented,
		 * and an unrecognised one leaves the list in server order rather than failing.
		 */
		getObserverEntities: builder.query<IApiResponse<IObserverLedgerRow[]>, IObserverEntityListParams>({
			query: ({ divisionType, isAssigned, hasApplicant, code, sortBy, sortOrder, PageNumber, PageSize, ...scope }) => ({
				url: '/ObserverAnalytics',
				params: {
					ElectionId: scope.electionId,
					ElectionObserverId: scope.electionObserverId,
					ResultType: OBSERVER_RESULT_TYPE_PARAM[divisionType],
					...(typeof isAssigned === 'boolean' ? { IsAssigned: isAssigned } : {}),
					...(typeof hasApplicant === 'boolean' ? { HasApplicant: hasApplicant } : {}),
					...(code ? { Code: code } : {}),
					...(sortBy ? { SortKey: sortBy } : {}),
					...(sortOrder ? { SortOrder: sortOrder } : {}),
					PageNumber,
					PageSize,
				},
			}),
			providesTags: ['ObserverManagement'],
		}),
		/**
		 * Applications, optionally narrowed to ONE entity at ONE level.
		 *
		 * <p>`entityCode` and `divisionType` were passed straight through until 2026-09-09, and
		 * only one of them could ever have worked. The server binds case-insensitively, so
		 * `entityCode` reaches `EntityCode` — but `divisionType` is not a name the DTO has under
		 * any casing, so the level was silently unfiltered. Both are now mapped explicitly, the
		 * same way `getObserverEntities` above does it.</p>
		 *
		 * <p>The level token is lowercase on the wire (`ec8a`), which is why it goes through
		 * `OBSERVER_RESULT_TYPE_PARAM` rather than `ObserverResultType` directly.</p>
		 *
		 * <p>Both are omitted when absent rather than sent empty: `EntityCode=` is a filter on the
		 * empty string to a server that binds it, which returns nothing — the opposite of the
		 * unscoped list the caller wanted.</p>
		 */
		getObserverApplications: builder.query<IApiResponse<IObserverApplicationSummary[]>, IObserverApplicationListParams>({
			query: (params) => ({ url: '/ObserverApplication', params: observerApplicationParams(params) }),
			providesTags: ['ObserverManagement'],
		}),
		/**
		 * One application in full. The scope ids ride along as query params so a Super Admin
		 * proxying for a partner is authorized the same way the list is; the server binds
		 * query params case-insensitively, so the camelCase names match its PascalCase DTO.
		 */
		getObserverApplicationById: builder.query<IApiResponse<IObserverApplicationDetail>, IObserverApplicationActionParams>({
			query: ({ id, ...scope }) => ({ url: `/ObserverApplication/${id}`, params: scope }),
			providesTags: ['ObserverManagement'],
		}),
		/**
		 * Approves one application. The id rides in the PATH and the scope in the query
		 * string, matching `getObserverApplicationById` — the server binds query params
		 * case-insensitively, so camelCase reaches its PascalCase DTO.
		 *
		 * No reason is collected: an approval needs no justification, and adding a prompt
		 * would make the common decision the slow one.
		 */
		approveObserverApplication: builder.mutation<IApiResponse, IObserverApplicationActionParams>({
			query: ({ id, ...scope }) => ({ url: `/ObserverApplication/${id}/approve`, method: 'POST', params: scope }),
			invalidatesTags: ['ObserverManagement'],
		}),
		/**
		 * Rejects one application. The reason is sent in the BODY and is unverified against
		 * the server's DTO — if it is ignored the rejection still lands, but the audit trail
		 * loses the justification the operator was required to type.
		 */
		rejectObserverApplication: builder.mutation<IApiResponse, IObserverReasonActionParams>({
			query: ({ id, reason, ...scope }) => ({
				url: `/ObserverApplication/${id}/reject`,
				method: 'POST',
				params: scope,
				body: { reason },
			}),
			invalidatesTags: ['ObserverManagement'],
		}),
		/**
		 * Approves many applications in one call. The body key is `observerApplicationIds`
		 * — not `applicationIds`, which is what every other action in this file calls them.
		 *
		 * Answers the agents' approve-bulk envelope — `{ data: { approved: number[], failed: [] } }`
		 * (observed on staging 2026-10-19), NOT the `succeeded`/`failed`/counts shape of the mocked
		 * bulk actions. Read it with `reconcileObserverApproveBulk`, never by counting fields.
		 */
		bulkApproveObserverApplications: builder.mutation<ObserverApproveBulkEnvelope, IObserverBulkApproveParams>({
			query: ({ applicationIds, ...scope }) => ({
				url: '/ObserverApplication/approve-bulk',
				method: 'POST',
				params: scope,
				body: { observerApplicationIds: applicationIds },
			}),
			invalidatesTags: ['ObserverManagement'],
		}),
		/**
		 * Moves lapsed offers on: holder → Offer Expired, Reserved → Offer Sent, earliest
		 * Waitlisted → Reserved. The backend runs NO timer — if nothing calls this, an expired
		 * offer never reaches the next applicant. Idempotent; see `useObserverOfferSweep`.
		 *
		 * Scope in the BODY here, unlike the query-string scope of approve / reject. Invalidates
		 * only when something moved, so a no-op sweep does not refetch every observer list.
		 */
		expireObserverOfferedLinks: builder.mutation<IApiResponse<IObserverOfferSweepResult>, IObserverManagementScopeParams>({
			query: (body) => ({ url: '/ObserverApplication/expire-offered-links', method: 'POST', body }),
			invalidatesTags: (result) =>
				result?.data?.expiredIds?.length || result?.data?.promotedIds?.length ? ['ObserverManagement'] : [],
		}),
		/**
		 * Creates a ready-to-use observer posted to one STATE — no application, no offer, no credit
		 * charged. The partner must already be registered for the election (that creates its state
		 * slots). Other in-play applicants for the state become Rejected, so every observer list and
		 * the coverage ledger are stale afterwards. Read errors with `readStateObserverError`.
		 */
		createObserverStateAgent: builder.mutation<IApiResponse<ICreateStateObserverResult>, ICreateStateObserverPayload>({
			query: (body) => ({ url: '/ObserverApplication/state-agent', method: 'POST', body }),
			invalidatesTags: ['ObserverManagement'],
		}),
		exportObserverApplications: builder.mutation<Blob, IObserverApplicationListParams>({
			queryFn: async (params) => ({ data: mockExportObserverApplications(params) }),
		}),

		// Observer Analytics endpoints
		getObserverAnalyticsSummary: builder.query({
			query: (params) => ({ url: '/ObserverAnalytics', params }),
		}),
		/**
		 * Per-division assignment rollups — the single source of truth behind the coverage
		 * cards. Tagged so an offer / rejection / release invalidates it: without this the
		 * cards keep reporting the ledger as it stood before the operator acted on it.
		 */
		getObserverAnalyticsAssignmentsStats: builder.query<IApiResponse<IObserverAssignmentStats>, IObserverAnalyticsParams>({
			query: (params) => ({ url: '/ObserverAnalytics/assignment-stats', params }),
			providesTags: ['ObserverManagement'],
		}),
	}),
});

export const {
	useGetElectionObserversQuery,
	useLazyGetElectionObserversQuery,
	useGetElectionObserverByIdQuery,
	useCreateElectionObserverMutation,
	useUpdateElectionObserverMutation,
	useDeleteElectionObserverMutation,
	useGetObserverRegistrationLinkQuery,
	useGenerateObserverLinkMutation,
	useDeleteObserverLinkMutation,
	useGetObserverElectionsQuery,
	useLazyGetObserverElectionsQuery,
	useRegisterObserverElectionMutation,
	useGetObserverEntitiesQuery,
	useGetObserverApplicationsQuery,
	useGetObserverApplicationByIdQuery,
	useApproveObserverApplicationMutation,
	useBulkApproveObserverApplicationsMutation,
	useRejectObserverApplicationMutation,
	useExpireObserverOfferedLinksMutation,
	useCreateObserverStateAgentMutation,
	useExportObserverApplicationsMutation,
	useAccreditElectionObserverMutation,

	useGetObserverAnalyticsSummaryQuery,
	useGetObserverAnalyticsAssignmentsStatsQuery,
} = electionObserverApi;

/** Temporary aliases retained for callers outside Manage Observers. */
export const useRejectObserverVolunteerMutation = useRejectObserverApplicationMutation;
